import React, { useState, useEffect } from 'react';
import { Gift, Play, FileText, Video, Image, Book, ChefHat, Dumbbell, Brain, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const FreeContent = () => {
  const [contents, setContents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedContent, setSelectedContent] = useState(null);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    fetchFreeContent();
  }, []);

  const fetchFreeContent = async () => {
    try {
      // Obtener todo el contenido que tiene 'free' en levels o access_options
      const response = await axios.get(`${BACKEND_URL}/api/content/free`);
      setContents(response.data);
    } catch (error) {
      console.error('Error fetching free content:', error);
      // Fallback: obtener todo el contenido y filtrar en frontend
      try {
        const allContent = await axios.get(`${BACKEND_URL}/api/content`);
        const freeContent = allContent.data.filter(content => 
          content.levels?.includes('free') || 
          content.access_options?.includes('free') ||
          content.level === 'free'
        );
        setContents(freeContent);
      } catch (e) {
        console.error('Fallback error:', e);
      }
    } finally {
      setLoading(false);
    }
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'academia': return <Book className="w-5 h-5" />;
      case 'ebooks': return <FileText className="w-5 h-5" />;
      case 'recetas': return <ChefHat className="w-5 h-5" />;
      case 'entrenamiento': return <Dumbbell className="w-5 h-5" />;
      case 'mindset': return <Brain className="w-5 h-5" />;
      case 'blog': return <FileText className="w-5 h-5" />;
      case 'challenge': return <Dumbbell className="w-5 h-5" />;
      default: return <Gift className="w-5 h-5" />;
    }
  };

  const getCategoryLabel = (category) => {
    const labels = {
      'academia': 'Academia',
      'ebooks': 'Ebook',
      'recetas': 'Receta',
      'entrenamiento': 'Rutina',
      'mindset': 'Mindset',
      'blog': 'Blog',
      'challenge': 'Challenge'
    };
    return labels[category] || category;
  };

  const getCategoryColor = (category) => {
    const colors = {
      'academia': 'bg-yellow-900/50 text-yellow-400',
      'ebooks': 'bg-red-900/50 text-red-400',
      'recetas': 'bg-green-900/50 text-green-400',
      'entrenamiento': 'bg-pink-900/50 text-pink-400',
      'mindset': 'bg-purple-900/50 text-purple-400',
      'blog': 'bg-orange-900/50 text-orange-400',
      'challenge': 'bg-blue-900/50 text-blue-400'
    };
    return colors[category] || 'bg-zinc-800 text-zinc-400';
  };

  const categories = ['all', ...new Set(contents.map(c => c.category).filter(Boolean))];

  const filteredContents = filter === 'all' 
    ? contents 
    : contents.filter(c => c.category === filter);

  const handleViewContent = (content) => {
    if (content.content_type === 'video') {
      setSelectedContent(content);
    } else {
      // Para PDFs e imágenes, abrir en nueva pestaña
      const url = content.file_path?.startsWith('http') ? content.file_path : `${BACKEND_URL}${content.file_path}`;
      window.open(url, '_blank');
    }
  };

  return (
    <div className="min-h-screen bg-[#09090b] py-24">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Header */}
        <div className="text-center mb-12">
          <Gift className="w-16 h-16 mx-auto text-pink-500 mb-4" />
          <h1 className="text-4xl md:text-5xl font-bold mb-4 text-white" style={{fontFamily: 'Syne, sans-serif'}} data-testid="free-content-title">
            Contenido Gratuito
          </h1>
          <p className="text-xl text-zinc-400 max-w-2xl mx-auto">
            ¡Por ser tu primera vez, accede a este contenido exclusivo totalmente gratis! 
            Ebooks de entreno, recetas saludables y mucho más.
          </p>
        </div>

        {/* Filtros */}
        {categories.length > 2 && (
          <div className="flex flex-wrap justify-center gap-2 mb-10">
            {categories.map(cat => (
              <button
                key={cat}
                onClick={() => setFilter(cat)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                  filter === cat 
                    ? 'bg-pink-600 text-white' 
                    : 'bg-zinc-800 text-zinc-300 hover:bg-zinc-700'
                }`}
              >
                {cat === 'all' ? 'Todo' : getCategoryLabel(cat)}
              </button>
            ))}
          </div>
        )}

        {/* Loading */}
        {loading ? (
          <div className="text-center py-20">
            <div className="w-12 h-12 border-4 border-pink-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-zinc-500">Cargando contenido gratuito...</p>
          </div>
        ) : filteredContents.length === 0 ? (
          <div className="text-center py-20">
            <Gift className="w-16 h-16 mx-auto text-zinc-600 mb-4" />
            <h3 className="text-xl font-bold mb-2 text-white">No hay contenido gratuito disponible</h3>
            <p className="text-zinc-500 mb-6">Pronto agregaremos más contenido para ti</p>
            <a href="/programs">
              <Button className="bg-pink-600 hover:bg-pink-500 text-white rounded-full px-8">
                Ver Programas
              </Button>
            </a>
          </div>
        ) : (
          /* Grid de contenido */
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredContents.map((content) => (
              <div 
                key={content.content_id} 
                className="bg-zinc-900 rounded-3xl overflow-hidden border border-zinc-800 hover:border-pink-500/50 transition-all"
                data-testid={`free-item-${content.content_id}`}
              >
                {/* Thumbnail */}
                <div className="aspect-video bg-zinc-800 relative overflow-hidden">
                  {content.cover_image || content.thumbnail_url ? (
                    <img 
                      src={(content.cover_image || content.thumbnail_url).startsWith('http') ? (content.cover_image || content.thumbnail_url) : `${BACKEND_URL}${content.cover_image || content.thumbnail_url}`}
                      alt={content.title}
                      className="w-full h-full object-cover"
                    />
                  ) : content.content_type === 'image' ? (
                    <img 
                      src={content.file_path.startsWith('http') ? content.file_path : `${BACKEND_URL}${content.file_path}`}
                      alt={content.title}
                      className="w-full h-full object-cover"
                    />
                  ) : content.content_type === 'video' ? (
                    <div className="w-full h-full flex items-center justify-center">
                      <Video className="w-16 h-16 text-pink-500" />
                    </div>
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <FileText className="w-16 h-16 text-pink-500" />
                    </div>
                  )}

                  {/* Badge categoría */}
                  <span className={`absolute top-3 left-3 px-3 py-1 rounded-full text-xs font-bold flex items-center gap-1 ${getCategoryColor(content.category)}`}>
                    {getCategoryIcon(content.category)}
                    {getCategoryLabel(content.category)}
                  </span>

                  {/* Badge tipo */}
                  <span className={`absolute top-3 right-3 px-2 py-1 rounded-full text-xs font-bold ${
                    content.content_type === 'video' ? 'bg-pink-600 text-white' : 'bg-blue-600 text-white'
                  }`}>
                    {content.content_type === 'video' ? '🎬 Video' : content.content_type === 'pdf' ? '📄 PDF' : '🖼️ Imagen'}
                  </span>

                  {/* Badge GRATIS */}
                  <span className="absolute bottom-3 right-3 bg-green-500 text-white px-3 py-1 rounded-full text-xs font-bold">
                    ✓ GRATIS
                  </span>
                </div>

                {/* Info */}
                <div className="p-5">
                  <h3 className="text-lg font-bold mb-2 line-clamp-2 text-white" style={{fontFamily: 'Poppins, sans-serif'}}>
                    {content.title}
                  </h3>
                  
                  {content.description && (
                    <p className="text-zinc-400 text-sm mb-4 line-clamp-2">
                      {content.description}
                    </p>
                  )}

                  {/* Info adicional para podcasts */}
                  {content.guest && (
                    <p className="text-sm text-purple-400 mb-2">🎤 Con: {content.guest}</p>
                  )}
                  {content.duration && (
                    <p className="text-sm text-zinc-500 mb-3">⏱️ {content.duration}</p>
                  )}

                  <Button 
                    onClick={() => handleViewContent(content)}
                    className="w-full bg-pink-600 hover:bg-pink-500 text-white rounded-full py-5 font-bold"
                  >
                    <Play className="w-4 h-4 mr-2" />
                    {content.content_type === 'video' ? 'Ver Video' : content.content_type === 'pdf' ? 'Ver PDF' : 'Ver Contenido'}
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* CTA */}
        <div className="mt-16 bg-gradient-to-r from-pink-600 to-pink-500 text-white rounded-3xl p-10 text-center">
          <h2 className="text-3xl font-bold mb-4" style={{fontFamily: 'Syne, sans-serif'}}>
            ¿Quieres acceso a todo el contenido?
          </h2>
          <p className="text-xl text-pink-100 mb-6 max-w-2xl mx-auto">
            Desbloquea videos exclusivos, rutinas completas, recetas premium y mucho más con nuestras membresías
          </p>
          <a href="/programs">
            <Button className="bg-white text-pink-600 hover:bg-pink-50 rounded-full px-10 py-6 text-lg font-bold">
              Ver Membresías
            </Button>
          </a>
        </div>
      </div>

      {/* Modal de Video */}
      {selectedContent && (
        <div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4">
          <div className="relative w-full max-w-5xl">
            <button 
              onClick={() => setSelectedContent(null)}
              className="absolute -top-12 right-0 text-white hover:text-pink-400 transition-colors"
            >
              <X className="w-8 h-8" />
            </button>
            
            <h3 className="text-white text-xl font-bold mb-4" style={{fontFamily: 'Syne, sans-serif'}}>
              {selectedContent.title}
            </h3>
            
            <div className="bg-black rounded-2xl overflow-hidden">
              {/* Verificar si es YouTube, Drive o video local */}
              {(() => {
                const videoUrl = selectedContent.file_path || selectedContent.video_url || selectedContent.external_url;
                
                // Si es YouTube
                if (videoUrl && (videoUrl.includes('youtube.com') || videoUrl.includes('youtu.be'))) {
                  // Convertir URL de YouTube a embed
                  let embedId = '';
                  if (videoUrl.includes('youtu.be/')) {
                    embedId = videoUrl.split('youtu.be/')[1]?.split('?')[0];
                  } else if (videoUrl.includes('watch?v=')) {
                    embedId = videoUrl.split('watch?v=')[1]?.split('&')[0];
                  } else if (videoUrl.includes('/embed/')) {
                    embedId = videoUrl.split('/embed/')[1]?.split('?')[0];
                  }
                  
                  return (
                    <iframe
                      className="w-full aspect-video"
                      src={`https://www.youtube.com/embed/${embedId}?autoplay=1`}
                      title={selectedContent.title}
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    />
                  );
                }
                
                // Si es Google Drive
                if (videoUrl && videoUrl.includes('drive.google.com')) {
                  // Convertir URL de Drive a embed
                  let fileId = '';
                  if (videoUrl.includes('/file/d/')) {
                    fileId = videoUrl.split('/file/d/')[1]?.split('/')[0];
                  } else if (videoUrl.includes('id=')) {
                    fileId = videoUrl.split('id=')[1]?.split('&')[0];
                  }
                  
                  return (
                    <iframe
                      className="w-full aspect-video"
                      src={`https://drive.google.com/file/d/${fileId}/preview`}
                      title={selectedContent.title}
                      frameBorder="0"
                      allow="autoplay"
                      allowFullScreen
                    />
                  );
                }
                
                // Video local del servidor
                const localUrl = videoUrl?.startsWith('http') ? videoUrl : `${BACKEND_URL}${videoUrl}`;
                return (
                  <video 
                    controls 
                    autoPlay
                    className="w-full max-h-[70vh]"
                    src={localUrl}
                  >
                    Tu navegador no soporta la reproducción de videos.
                  </video>
                );
              })()}
            </div>
            
            {selectedContent.description && (
              <p className="text-zinc-400 mt-4">
                {selectedContent.description}
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default FreeContent;
